import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore
} from "react";
import { getStorageKey } from "./auto-save/getStorageKey";
import { readLegacyLayout } from "./readLegacyLayout";
import type {
  Layout,
  LayoutChangedMeta,
  LayoutStorage,
  OnGroupLayoutChange,
  OnGroupLayoutChanged
} from "./types";

/**
 * Saves and restores group layouts between page loads.
 * It can be configured to store values using `localStorage`, `sessionStorage`, cookies, or any other persistence layer that makes sense for your application.
 */
export function useDefaultLayout({
  debounceSaveMs = 100,
  onlySaveAfterUserInteractions,
  panelIds,
  storage = localStorage,
  ...rest
}: {
  /**
   * Debounce save operation by the specified number of milliseconds; defaults to 100ms
   *
   * @deprecated Use the {@link onLayoutChanged} callback instead; it does not require debouncing
   */
  debounceSaveMs?: number;

  /**
   * Only auto-save layouts that were directly caused by user input (e.g. keyboard or mouse events).
   * Ignore layout changes resulting from imperative API calls or window resize events.
   */
  onlySaveAfterUserInteractions?: boolean;

  /**
   * For Groups that contain conditionally-rendered Panels, this prop can be used to save and restore multiple layouts.
   *
   * ℹ️ This prevents layout shift for server-rendered apps.
   *
   * ⚠️ Panel ids must match the Panels rendered within the Group during mount or the initial layout will be incorrect.
   */
  panelIds?: string[] | undefined;

  /**
   * Storage implementation; supports localStorage, sessionStorage, and custom implementations
   * Refer to documentation site for example integrations.
   *
   */
  storage?: LayoutStorage;
} & (
  | {
      /**
       * Group id; must be unique in order for layouts to be saved separately.
       * @deprecated Use the {@link id} param instead
       */
      groupId: string;
    }
  | {
      /**
       * Uniquely identifies a specific group/layout.
       */
      id: string;
    }
)) {
  const hasPanelIds = panelIds !== undefined;
  const id = "id" in rest ? rest.id : rest.groupId;

  const readStorageKey = getStorageKey(id, panelIds ?? []);

  // In the event that a client-only storage API is provided,
  // useSyncExternalStore prevents server/client hydration mismatch warning
  // This is not ideal; if possible a server-friendly storage API should be used
  // Try to read v4 Layout format first
  const defaultLayoutString = useSyncExternalStore(
    subscribe,
    () => storage.getItem(readStorageKey),
    () => storage.getItem(readStorageKey)
  );
  const defaultLayoutModern = useMemo(() => {
    if (defaultLayoutString) {
      // Rule out false positive for legacy layout format
      const parsed = JSON.parse(defaultLayoutString);
      const values = Object.values(parsed);
      if (Array.from(values).every((value) => typeof value === "number")) {
        return parsed as Layout;
      }
    }
  }, [defaultLayoutString]);

  // If not v4 layout was found, check for legacy v3 layout format
  const defaultLayoutLegacy = useMemo(() => {
    if (defaultLayoutModern) {
      return undefined;
    }

    return readLegacyLayout({
      id,
      panelIds,
      storage
    });
  }, [defaultLayoutModern, id, panelIds, storage]);

  const defaultLayout = defaultLayoutModern ?? defaultLayoutLegacy;

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearPendingTimeout = useCallback(() => {
    const timeout = timeoutRef.current;
    if (timeout) {
      timeoutRef.current = null;

      clearTimeout(timeout);
    }
  }, []);

  useLayoutEffect(() => {
    return () => {
      clearPendingTimeout();
    };
  }, [clearPendingTimeout]);

  const onLayoutChanged = useCallback<NonNullable<OnGroupLayoutChanged>>(
    // The hook persists every layout commit -- including library-driven ones --
    // because it owns its own storage and the goal is to remember whatever
    // layout the user is currently looking at. Consumers that only want to
    // persist on user interaction should branch on `isUserInteraction` in
    // their own callback (see #716) rather than via this hook.
    (layout: Layout, meta: LayoutChangedMeta) => {
      if (onlySaveAfterUserInteractions && !meta.isUserInteraction) {
        return;
      }

      clearPendingTimeout();

      let writeStorageKey: string;
      if (hasPanelIds) {
        writeStorageKey = getStorageKey(id, Object.keys(layout));
      } else {
        writeStorageKey = getStorageKey(id, []);
      }

      try {
        storage.setItem(writeStorageKey, JSON.stringify(layout));
      } catch (error) {
        console.error(error);
      }
    },
    [
      clearPendingTimeout,
      hasPanelIds,
      id,
      onlySaveAfterUserInteractions,
      storage
    ]
  );

  // TODO Deprecated; remove this in the future release
  const onLayoutChange = useCallback<NonNullable<OnGroupLayoutChange>>(
    (layout: Layout) => {
      clearPendingTimeout();

      if (debounceSaveMs === 0) {
        onLayoutChanged(layout, { isUserInteraction: false });
      } else {
        timeoutRef.current = setTimeout(() => {
          onLayoutChanged(layout, { isUserInteraction: false });
        }, debounceSaveMs);
      }
    },
    [clearPendingTimeout, debounceSaveMs, onLayoutChanged]
  );

  return {
    /**
     * Pass this value to `Group` as the `defaultLayout` prop.
     */
    defaultLayout,

    /**
     * Attach this callback on the `Group` as the `onLayoutChange` prop.
     *
     * @deprecated Use the {@link onLayoutChanged} prop instead.
     */
    onLayoutChange,

    /**
     * Attach this callback on the `Group` as the `onLayoutChanged` prop.
     */
    onLayoutChanged
  };
}

function subscribe() {
  return function unsubscribe() {};
}
